import type { ModalLoadQueryData } from '../../common/types';
import useFetchCreateConfigQuery from '../fetch-create-config-query';
import useFetchCreateConfigQueryInContext from '../fetch-create-config-query-in-context';
import useFetchSubtaskCreateConfigQuery from '../fetch-subtask-create-config-query';
import type { Props } from './types';

const useModalLoadQuery = (props: Props): ModalLoadQueryData => {
	const {
		displayOnlyRequiredFields = false,
		triggerPointKey,
		isSubTaskCreationOpen = false,
		triggerIssueCreatePayload,
		isModalLoadInContext = false,
	} = props;
	const [
		{
			isLoading,
			fieldConfig,
			fieldTabs,
			contextFields,
			meta,
			error,
			apiLatency,
			additionalServices,
		},
		fetchConfig,
	] = useFetchCreateConfigQuery({ displayOnlyRequiredFields, triggerPointKey });

	const [
		{
			isLoading: isSubtaskLoading,
			fieldConfig: subtaskFieldConfig,
			fieldTabs: subtaskFieldTabs,
			contextFields: subtaskContextFields,
			meta: subtaskFieldsDataMeta,
			error: subtaskFieldsDataError,
			apiLatency: subtaskApiLatency,
			additionalServices: subtaskAdditionalServices,
		},
		fetchSubtaskConfig,
	] = useFetchSubtaskCreateConfigQuery({ displayOnlyRequiredFields, triggerPointKey });

	const [
		{
			isLoading: isLoadingInContext,
			fieldConfig: fieldConfigInContext,
			fieldTabs: fieldTabsInContext,
			contextFields: contextFieldsInContext,
			meta: fieldsDataMetaInContext,
			error: fieldsDataErrorInContext,
			apiLatency: apiLatencyInContext,
			additionalServices: additionalServicesInContext,
		},
		fetchConfigInContext,
	] = useFetchCreateConfigQueryInContext({ displayOnlyRequiredFields, triggerPointKey });

	const parentId = triggerIssueCreatePayload?.parentId || null;
	const additionalContext = triggerIssueCreatePayload?.additionalContext || null;
	if (isModalLoadInContext && additionalContext) {
		return [
			{
				isLoading: isLoadingInContext,
				fieldConfig: fieldConfigInContext,
				fieldTabs: fieldTabsInContext,
				contextFields: contextFieldsInContext,
				meta: fieldsDataMetaInContext,
				error: fieldsDataErrorInContext,
				apiLatency: apiLatencyInContext,
				additionalServices: additionalServicesInContext,
			},
			fetchConfigInContext,
		];
	}
	if (isSubTaskCreationOpen && parentId) {
		return [
			{
				isLoading: isSubtaskLoading,
				fieldConfig: subtaskFieldConfig,
				fieldTabs: subtaskFieldTabs,
				contextFields: subtaskContextFields,
				meta: subtaskFieldsDataMeta,
				error: subtaskFieldsDataError,
				apiLatency: subtaskApiLatency,
				additionalServices: subtaskAdditionalServices,
			},
			fetchSubtaskConfig,
		];
	}
	return [
		{
			isLoading,
			fieldConfig,
			fieldTabs,
			contextFields,
			meta,
			error,
			apiLatency,
			additionalServices,
		},
		fetchConfig,
	];
};

export default useModalLoadQuery;
